import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { downloadMedia } from '@/lib/contentStudio';
import ZStudioNav from '@/pages/dashboard/apps/zstudio/components/ZStudioNav';
import {
  listZProjects,
  listZCreations,
  deleteZProject,
  createZProject,
  Z_MEDIA_KIND_META,
  type ZStudioProject,
  type ZStudioCreation,
} from '@/lib/zstudio';

export default function ZStudioProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<ZStudioProject[]>([]);
  const [creations, setCreations] = useState<ZStudioCreation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [p, c] = await Promise.all([listZProjects(user.id), listZCreations(user.id)]);
      setProjects(p);
      setCreations(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const projectCreations = (id: number) => creations.filter((c) => c.project_id === id);

  const duplicate = async (p: ZStudioProject) => {
    if (!user?.id) return;
    try { await createZProject({ ownerId: user.id, name: `${p.name} (copie)`, kind: p.kind, style: p.style || undefined, aspectRatio: p.aspect_ratio, sourceType: p.source_type || undefined, brief: p.brief || undefined }); load(); } catch { /* ignore */ }
  };

  const remove = async (p: ZStudioProject) => {
    if (!window.confirm('Supprimer ce projet et ses créations ?')) return;
    setProjects((prev) => prev.filter((x) => x.id !== p.id));
    try { await deleteZProject(p.id); } catch { load(); }
  };

  if (!user?.id) return <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
          <i className="ri-folders-line text-accent-500"></i>Projets
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Reprenez un travail ultérieurement : chaque projet regroupe ses créations et son contexte (spec 68 & 38).</p>
      </div>
      <ZStudioNav />

      {error && <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg"><i className="ri-error-warning-line"></i>{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-accent-500"></i></div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-dashed border-background-300/70 rounded-xl text-center">
          <span className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-folders-line text-2xl text-foreground-400"></i></span>
          <p className="text-sm font-medium text-foreground-700">Votre studio est prêt.</p>
          <p className="text-xs text-foreground-500 mt-1">Créez un projet pour commencer.</p>
          <Link to="/dashboard/zstudio/wizard" className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-magic-line"></i>Créer maintenant</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {projects.map((p) => {
            const items = projectCreations(p.id);
            const cover = items.find((c) => c.thumbnail_url || (c.media_kind === 'image' && c.url));
            return (
              <div key={p.id} className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden flex flex-col">
                <div className="aspect-video bg-background-100 flex items-center justify-center overflow-hidden">
                  {cover ? <img src={cover.thumbnail_url || (cover.url as string)} alt={p.name} className="w-full h-full object-cover object-top" /> : <i className="ri-folder-3-line text-3xl text-foreground-400"></i>}
                </div>
                <div className="p-3 flex-1 flex flex-col">
                  <p className="text-sm font-medium text-foreground-900 truncate">{p.name}</p>
                  <p className="text-[11px] text-foreground-500 truncate">{p.kind} · {p.aspect_ratio}{p.style ? ` · ${p.style}` : ''}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {items.slice(0, 4).map((c) => {
                      const meta = Z_MEDIA_KIND_META[c.media_kind || ''] || Z_MEDIA_KIND_META.text;
                      return <span key={c.id} className={`text-[10px] px-1.5 py-0.5 rounded-full ${meta.className}`}><i className={meta.icon}></i> {meta.label}</span>;
                    })}
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-500">{items.length} création(s)</span>
                  </div>
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-background-200/70">
                    <Link to="/dashboard/zstudio/creations" className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-eye-line"></i>Ouvrir</Link>
                    <button type="button" onClick={() => duplicate(p)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer" title="Dupliquer"><i className="ri-file-copy-line"></i></button>
                    {cover && (
                      <button type="button" onClick={() => downloadMedia(cover.thumbnail_url || (cover.url as string), `${p.name}.png`)} className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Télécharger"><i className="ri-download-2-line"></i></button>
                    )}
                    <button type="button" onClick={() => remove(p)} className="w-8 h-8 ml-auto flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Supprimer"><i className="ri-delete-bin-line"></i></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}